<template>
  <main class="review">
    <div class="review__inner">
      <button type="button" class="review__back" @click="goBack">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M5 12H19" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M13 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span>خروج از مرور</span>
      </button>

      <LoadingSpinner v-if="loading" message="در حال بارگذاری کارت‌ها..." />

      <ErrorMessage
        v-else-if="errorMessage"
        :message="errorMessage"
        @retry="load"
      />

      <EmptyState
        v-else-if="finished || !current"
        title="مرور امروز تمام شد"
        message="کارت آماده‌ای باقی نمانده. بعداً دوباره سر بزن."
      >
        <button type="button" class="review__done-btn" @click="goOverview">
          بازگشت به جعبه‌ها
        </button>
      </EmptyState>

      <template v-else>
        <div class="review__progress" aria-label="پیشرفت مرور">
          <div class="review__progress-track">
            <span :style="{ width: progressWidth }"></span>
          </div>
          <p class="review__progress-text">
            {{ toPersianDigits(reviewedCount) }} از {{ toPersianDigits(totalInSession) }}
          </p>
        </div>

        <button
          type="button"
          class="review__card"
          :class="{ 'review__card--flipped': flipped }"
          @click="flipped = !flipped"
        >
          <div class="review__card-face review__card-face--front">
            <span v-if="current.box" class="review__badge">جعبه {{ toPersianDigits(current.box) }}</span>
            <h2 class="review__word">{{ current.word }}</h2>
            <p v-if="current.phonetic" class="review__phonetic">{{ current.phonetic }}</p>
            <p v-if="current.part_of_speech" class="review__pos">{{ current.part_of_speech }}</p>
            <p class="review__hint">برای دیدن معنی کلیک کن</p>
          </div>
          <div class="review__card-face review__card-face--back">
            <span v-if="current.box" class="review__badge">جعبه {{ toPersianDigits(current.box) }}</span>
            <h2 class="review__definition">{{ current.definition }}</h2>
            <p v-if="current.example" class="review__example">“{{ current.example }}”</p>
          </div>
        </button>

        <div class="review__actions">
          <button
            type="button"
            class="review__btn review__btn--unknown"
            :disabled="submitting"
            @click="answer(false)"
          >
            بلد نیستم
          </button>
          <button
            type="button"
            class="review__btn review__btn--known"
            :disabled="submitting"
            @click="answer(true)"
          >
            بلدم
          </button>
        </div>
      </template>
    </div>
  </main>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { getLeitnerReview, submitLeitnerReview } from "@/api/leitner";
import { handleError } from "@/services/errorHandler";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import ErrorMessage from "@/components/common/ErrorMessage.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import type { VocabCard } from "@/types";

const router = useRouter();
const cards = ref<VocabCard[]>([]);
const index = ref(0);
const flipped = ref(false);
const loading = ref(true);
const submitting = ref(false);
const finished = ref(false);
const errorMessage = ref<string | null>(null);
const reviewedCount = ref(0);

const current = computed(() => cards.value[index.value] || null);
const totalInSession = computed(() => cards.value.length);
const progressWidth = computed(() => {
  if (!totalInSession.value) return "0%";
  return `${Math.round((reviewedCount.value / totalInSession.value) * 100)}%`;
});

function toPersianDigits(value: number | string): string {
  return String(value).replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);
}

async function load() {
  loading.value = true;
  errorMessage.value = null;
  finished.value = false;
  index.value = 0;
  reviewedCount.value = 0;
  flipped.value = false;
  try {
    const session = await getLeitnerReview(20);
    cards.value = session.cards || [];
    if (!cards.value.length) finished.value = true;
  } catch (e) {
    errorMessage.value = handleError(e, "LeitnerReview.load");
  } finally {
    loading.value = false;
  }
}

async function answer(known: boolean) {
  const card = current.value;
  if (!card || submitting.value) return;
  submitting.value = true;
  try {
    await submitLeitnerReview(card.id, known);
    reviewedCount.value += 1;
    flipped.value = false;
    if (index.value + 1 >= cards.value.length) {
      finished.value = true;
    } else {
      index.value += 1;
    }
  } catch (e) {
    errorMessage.value = handleError(e, "LeitnerReview.answer");
  } finally {
    submitting.value = false;
  }
}

function goBack() {
  router.push("/dashboard/leitner");
}

function goOverview() {
  router.push("/dashboard/leitner");
}

onMounted(load);
</script>

<style scoped>
.review {
  --color-primary: #6D5EF8;
  --color-bg: #FFFDF8;
  --color-card: #FFFFFF;
  --color-border: #ECE4D9;
  --color-text: #4C372A;
  --color-muted: #8C8176;

  min-height: 100vh;
  background: var(--color-bg);
  color: var(--color-text);
  font-family: Vazirmatn, "Segoe UI", Tahoma, sans-serif;
  padding: 48px 16px 80px;
  direction: rtl;
  box-sizing: border-box;
}

.review__inner {
  max-width: 560px;
  margin-inline: auto;
}

.review__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--color-card);
  border: 1px solid var(--color-border);
  color: var(--color-text);
  font-size: 14px;
  font-weight: 500;
  padding: 9px 18px;
  border-radius: 999px;
  cursor: pointer;
  margin-bottom: 28px;
}

.review__progress {
  margin-bottom: 18px;
}

.review__progress-track {
  height: 6px;
  border-radius: 999px;
  background: #F0EBE3;
  overflow: hidden;
}

.review__progress-track span {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #7C4DFF, #6D5EF8);
}

.review__progress-text {
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--color-muted);
  text-align: center;
}

.review__card {
  width: 100%;
  min-height: 320px;
  border: 1px solid var(--color-border);
  border-radius: 24px;
  background: var(--color-card);
  box-shadow: 0 10px 28px rgba(76, 55, 42, 0.08);
  cursor: pointer;
  padding: 28px 24px;
  text-align: center;
  position: relative;
  font-family: inherit;
  color: inherit;
}

.review__card-face--back {
  display: none;
}

.review__card--flipped .review__card-face--front {
  display: none;
}

.review__card--flipped .review__card-face--back {
  display: block;
}

.review__badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  background: #FBF7FC;
  color: #7C4DFF;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 18px;
}

.review__word {
  margin: 0 0 10px;
  font-size: 36px;
  font-weight: 700;
  direction: ltr;
}

.review__phonetic {
  margin: 0 0 8px;
  color: var(--color-muted);
  direction: ltr;
  font-size: 14px;
}

.review__pos {
  margin: 0 0 24px;
  color: #7C4DFF;
  font-size: 13px;
  font-weight: 600;
  direction: ltr;
}

.review__hint {
  margin: 0;
  color: var(--color-muted);
  font-size: 13px;
}

.review__definition {
  margin: 0 0 16px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.5;
  direction: ltr;
}

.review__example {
  margin: 0;
  color: var(--color-muted);
  font-size: 14px;
  line-height: 1.7;
  direction: ltr;
}

.review__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 20px;
}

.review__btn {
  border: 1px solid var(--color-border);
  border-radius: 14px;
  padding: 14px 12px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}

.review__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.review__btn--unknown {
  background: #FFF7F2;
  color: #C45C5C;
  border-color: #F0D2C8;
}

.review__btn--known {
  background: #F3FBF1;
  color: #3F8F4E;
  border-color: #CDE8C8;
}

.review__done-btn {
  margin-top: 12px;
  border: none;
  border-radius: 12px;
  background: var(--color-primary);
  color: #fff;
  font-family: inherit;
  font-weight: 700;
  padding: 12px 18px;
  cursor: pointer;
}
</style>
